/**
 * AnnouncementListPage — Issue #365.
 *
 * Lists administrative announcements with create (draft), publish, retract,
 * archive and read/acknowledge actions. Read receipts surface as a summary
 * card in the announcement detail view.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_AFFAIRS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAffairsPermissions';
import {
  ADMIN_ANNOUNCEMENT_SCOPE_LABELS,
  ADMIN_ANNOUNCEMENT_STATUS_LABELS,
  useAdminAnnouncementMutations,
  useAdminAnnouncementReceipts,
  useAdminAnnouncements,
  type AdminAnnouncementDto,
} from '@/hooks/useAdminAffairs';

const STATUS_BADGE: Readonly<Record<AdminAnnouncementDto['status'], string>> = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-emerald-100 text-emerald-700',
  retracted: 'bg-amber-100 text-amber-800',
  archived: 'bg-zinc-100 text-zinc-500',
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN');
}

export default function AnnouncementListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.ANNOUNCEMENT_READ,
  );
  const canWrite = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.ANNOUNCEMENT_WRITE,
  );
  const canManage = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.ANNOUNCEMENT_MANAGE,
  );
  const canSeeReceipts = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.READ,
  );

  const { items, total, loading, error, fetchAnnouncements } =
    useAdminAnnouncements();
  const {
    loading: actionLoading,
    error: actionError,
    create,
    publish,
    retract,
    archive,
    markRead,
  } = useAdminAnnouncementMutations();

  const [statusFilter, setStatusFilter] = useState<
    AdminAnnouncementDto['status'] | ''
  >('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: receipts, fetchReceipts } =
    useAdminAnnouncementReceipts(selectedId ?? undefined);

  useEffect(() => {
    if (!canRead) return;
    void fetchAnnouncements({ status: statusFilter || undefined });
  }, [canRead, statusFilter, fetchAnnouncements]);

  useEffect(() => {
    if (selectedId && canSeeReceipts) {
      void fetchReceipts(selectedId);
    }
  }, [selectedId, canSeeReceipts, fetchReceipts]);

  if (!canRead) return <AccessDenied resource="行政公告" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">行政公告</h1>
          <p className="text-sm text-slate-500">
            起草 / 发布行政公告，跟踪员工阅读 / 确认；公告内容会在后端做简易脱敏。
          </p>
        </div>
        {canWrite ? (
          <button
            type="button"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            data-testid="announcement-create-entry"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? '取消' : '新增公告 →'}
          </button>
        ) : null}
      </header>

      {creating && canWrite ? (
        <Card className="space-y-3 p-4">
          <label className="block text-sm">
            标题
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              data-testid="announcement-create-title"
            />
          </label>
          <label className="block text-sm">
            正文
            <textarea
              rows={6}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.content}
              onChange={(e) =>
                setForm((f) => ({ ...f, content: e.target.value }))
              }
              data-testid="announcement-create-content"
            />
          </label>
          <button
            type="button"
            disabled={actionLoading}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
            data-testid="announcement-create-submit"
            onClick={async () => {
              const created = await create({
                title: form.title,
                content: form.content,
              });
              if (created) {
                setForm({ title: '', content: '' });
                setCreating(false);
                await fetchAnnouncements({
                  status: statusFilter || undefined,
                });
              }
            }}
          >
            {actionLoading ? '提交中…' : '保存草稿'}
          </button>
          {actionError ? (
            <p
              className="text-sm text-red-600"
              data-testid="announcement-create-error"
            >
              {actionError.message}
            </p>
          ) : null}
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                (e.target.value as AdminAnnouncementDto['status']) || '',
              )
            }
            data-testid="announcement-status-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_ANNOUNCEMENT_STATUS_LABELS) as [
                AdminAnnouncementDto['status'],
                string,
              ][]
            ).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">标题</th>
              <th className="px-3 py-2">范围</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">发布时间</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={5}
                  data-testid="announcement-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={5}
                  data-testid="announcement-empty"
                >
                  暂无公告
                </td>
              </tr>
            ) : (
              items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      data-testid={`announcement-select-${a.id}`}
                      onClick={() => setSelectedId(a.id)}
                    >
                      {a.title}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {ADMIN_ANNOUNCEMENT_SCOPE_LABELS[a.scopeType]}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE[a.status]}`}
                    >
                      {ADMIN_ANNOUNCEMENT_STATUS_LABELS[a.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {fmtDateTime(a.publishedAt)}
                  </td>
                  <td className="space-x-2 px-3 py-2 text-xs">
                    {a.status === 'draft' && canManage ? (
                      <button
                        type="button"
                        className="text-emerald-600 hover:underline"
                        data-testid={`announcement-publish-${a.id}`}
                        onClick={async () => {
                          await publish(a.id, a.version);
                          await fetchAnnouncements({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        发布
                      </button>
                    ) : null}
                    {a.status === 'published' && canWrite ? (
                      <button
                        type="button"
                        className="text-amber-600 hover:underline"
                        data-testid={`announcement-retract-${a.id}`}
                        onClick={async () => {
                          const reason = window.prompt('撤回原因（可选）');
                          await retract(a.id, a.version, reason ?? undefined);
                          await fetchAnnouncements({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        撤回
                      </button>
                    ) : null}
                    {a.status === 'retracted' && canManage ? (
                      <button
                        type="button"
                        className="text-emerald-600 hover:underline"
                        data-testid={`announcement-republish-${a.id}`}
                        onClick={async () => {
                          await publish(a.id, a.version);
                          await fetchAnnouncements({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        重新发布
                      </button>
                    ) : null}
                    {a.status !== 'archived' && canManage ? (
                      <button
                        type="button"
                        className="text-slate-600 hover:underline"
                        data-testid={`announcement-archive-${a.id}`}
                        onClick={async () => {
                          const reason = window.prompt('归档原因（可选）');
                          await archive(a.id, a.version, reason ?? undefined);
                          await fetchAnnouncements({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        归档
                      </button>
                    ) : null}
                    {a.status === 'published' ? (
                      <>
                        <button
                          type="button"
                          className="text-blue-600 hover:underline"
                          data-testid={`announcement-read-${a.id}`}
                          onClick={async () => {
                            await markRead(a.id, false);
                          }}
                        >
                          标记已读
                        </button>
                        <button
                          type="button"
                          className="text-blue-700 hover:underline"
                          data-testid={`announcement-ack-${a.id}`}
                          onClick={async () => {
                            await markRead(a.id, true);
                          }}
                        >
                          确认
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-500">合计 {total} 条公告</p>

      {selectedId && canSeeReceipts ? (
        <Card className="space-y-2 p-4" data-testid="announcement-receipt-card">
          <h2 className="text-base font-medium">回执汇总</h2>
          {receipts ? (
            <p className="text-sm text-slate-600">
              总计 {receipts.summary.total} 人，已确认 {receipts.summary.acknowledged}
              ，已读 {receipts.summary.read}，未读 {receipts.summary.unread}
            </p>
          ) : (
            <p className="text-sm text-slate-500">暂未加载回执</p>
          )}
        </Card>
      ) : null}
    </div>
  );
}
